import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Featured merch strip on the homepage.
 *
 * Pulls up to 6 featured products from /api/merch/products?featured=true and
 * renders them as chunky Sprite-Vault-styled bubbles. Clicking through the
 * "See all" CTA (or any card) opens the full /merch page. If nothing is
 * marked featured, the strip stays hidden — no empty-state noise on the home.
 */
export default function MerchStrip() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.listMerch({ featured: true, limit: 6 });
        if (alive) setItems(list);
      } catch {
        // fail silent — homepage strip is optional
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-3 sm:px-6 mt-6 sm:mt-10"
      data-testid="homepage-merch-strip"
    >
      <div
        className="rounded-2xl border-[3px] shadow-[0_6px_0_var(--t-card-border)] p-4 sm:p-6"
        style={{
          background: 'linear-gradient(135deg, #14002e 0%, #3a1064 100%)',
          borderColor: 'var(--t-card-border)',
        }}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#ffd900] to-[#ff9a1c] border-2 border-[#0a0018] shadow-[0_3px_0_#0a0018] flex items-center justify-center">
              <ShoppingBag className="h-5 w-5 text-[#0a0018]" />
            </div>
            <div>
              <div className="font-black uppercase tracking-widest text-[10px] sm:text-xs text-[#ffd900]">
                🔥 Hot Merch For Sale
              </div>
              <div className="font-black text-white text-lg sm:text-2xl leading-tight">
                Sprite Vault Shop
              </div>
            </div>
          </div>
          <Link
            to="/merch"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#ffd900] text-[#0a0018] px-4 py-2 font-black uppercase tracking-widest text-[11px] sm:text-xs border-2 border-[#0a0018] shadow-[0_3px_0_#0a0018]"
            data-testid="merch-strip-see-all"
          >
            See all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(loading ? Array.from({ length: 4 }) : items).map((p, i) => (
            <MerchBubble key={p?.id || i} product={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function MerchBubble({ product }) {
  if (!product) {
    return (
      <div className="rounded-2xl aspect-square bg-white/5 border-2 border-white/10 animate-pulse" />
    );
  }
  const price = product.price_cents == null
    ? null
    : `$${(product.price_cents / 100).toFixed(2)}`;
  const isExternal = product.inventory_type !== 'stripe' && !!product.external_url;
  const inner = (
    <div
      className="rounded-2xl overflow-hidden border-[3px] border-white/25 group-hover:border-[#ffd900] transition-all shadow-[0_4px_0_rgba(0,0,0,0.5)] group-hover:-translate-y-0.5"
      data-testid={`merch-bubble-${product.id}`}
    >
      <div className="relative aspect-square bg-gradient-to-br from-[#3a1064] to-[#14002e]">
        {product.image ? (
          <img
            src={product.image}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <ShoppingBag className="h-8 w-8 text-white/30" />
          </div>
        )}
        {isExternal && (
          <div className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 flex items-center justify-center">
            <ExternalLink className="h-3 w-3 text-white/80" />
          </div>
        )}
      </div>
      <div className="p-2 bg-white/10 backdrop-blur">
        <div className="font-black text-white text-[11px] leading-tight line-clamp-2 min-h-[2.4em]">
          {product.title}
        </div>
        {price && (
          <div className="mt-1 inline-block rounded-full bg-[#ffd900] text-[#0a0018] font-black px-2 py-0.5 text-[10px] border-2 border-[#0a0018]">
            {price}
          </div>
        )}
      </div>
    </div>
  );

  if (isExternal) {
    return (
      <a
        href={product.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
      >
        {inner}
      </a>
    );
  }
  return <Link to="/merch" className="block group">{inner}</Link>;
}
